import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import { Validator } from "Utils/validator";
import { VIEW_PROFILE_SECTIONS } from "Constant/constants";

export const BasicDetailsModal = ({
  show,
  basicDetailsData,
  toggle,
  fetchData,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [errMsg, setErrMsg] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const { firstName, lastName, email, phone } = formData;

  const validationRequiredFields = {
    firstName: {
      blankCheck: Validator.text,
    },
    lastName: {
      blankCheck: Validator.text,
    },
    phone: {
      blankCheck: Validator.text,
    },
  };

  useEffect(() => {
    if (basicDetailsData) {
      setFormData({
        firstName: basicDetailsData.firstName || "",
        lastName: basicDetailsData.lastName || "",
        email: basicDetailsData.email || "",
        phone: basicDetailsData.phone || "",
      });
    }
  }, [basicDetailsData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const fieldRule = validationRequiredFields[name];

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (fieldRule) {
      if (fieldRule.blankCheck && !fieldRule.blankCheck(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(`common.messages.error.common.${name}.blank`),
        }));
      } else if (fieldRule?.inValidCheck && !fieldRule.inValidCheck(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(`common.messages.error.common.${name}.inValid`),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      for (const field in validationRequiredFields) {
        const value = formData[field];
        const rule = validationRequiredFields[field];

        // Blank check
        if (rule.blankCheck && !rule.blankCheck(value)) {
          errors[field] = t(`common.messages.error.common.${field}.blank`);
          isValid = false;
        }
        // Invalid format check (only if blank check passed)
        // else if (rule.inValidCheck && !rule.inValidCheck(value)) {
        //   errors[field] = t(`common.messages.error.common.${field}.inValid`);
        //   isValid = false;
        // }
      }

      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSavePersonalDetails = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.personalDetails,
          [VIEW_PROFILE_SECTIONS.personalDetails]: {
            ...basicDetailsData,
            firstName,
            lastName,
            email,
            phone,
          },
        };
        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t("auth.viewProfile.editBasicDetails")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="firstName" className="form-label">
                {t("common.label.firstName")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="firstName"
                name="firstName"
                placeholder={t("common.placeHolder.firstName")}
                value={firstName}
                onChange={handleChange}
              />
              {errMsg.firstName && <p className="error">{errMsg.firstName}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="lastName" className="form-label">
                {t("common.label.lastName")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="lastName"
                name="lastName"
                placeholder={t("common.placeHolder.lastName")}
                value={lastName}
                onChange={handleChange}
              />
              {errMsg.lastName && <p className="error">{errMsg.lastName}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                {t("common.label.email")} <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                placeholder={t("common.placeHolder.email")}
                value={email}
                disabled
              />
              {errMsg.email && <p className="error">{errMsg.email}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="phone" className="form-label">
                {t("common.label.contactNo")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="phone"
                name="phone"
                placeholder={t("common.placeHolder.phone")}
                value={phone}
                onChange={handleChange}
              />
              {errMsg.phone && <p className="error">{errMsg.phone}</p>}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button variant="primary" onClick={onSavePersonalDetails}>
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
